import { useMemo, useState } from 'react';

import { useDebounceCallback } from 'usehooks-ts';

import { ID_WIDTH_ATTRIBUTE } from '@shared/constants/keys';
import { useAppDispatch, useAppSelector } from '@shared/hooks/redux';
import { Metrics } from '@shared/types/enums/metrics.enum';
import { OptionType } from '@shared/types/global';
import { Container } from '@shared/ui/Container';
import { HStack } from '@shared/ui/Stack';
import { sendMetrics } from '@shared/utils/sendMetrics/sendMetrics';

import { changeList, changeRange, submitFilters } from '../../model/slice/categorySlice';
import { TransformedCategoryFilterItemType } from '../../model/types/categoryFilters';
import { getFilterLabel } from '../../utils/getFilterLabel/getFilterLabel';
import { getImportantFiltersForCategory } from '../../utils/getImportantFiltersForCategory/getImportantFiltersForCategory';
import { AllFiltersButton } from './AllFiltersButton/AllFiltersButton';
import { FilterModal } from './FilterModal/FilterModal';
import { FilterSelect } from './FilterSelect/FilterSelect';
import { SelectWithRange } from './SelectWithRange/SelectWithRange';
import cls from './Filter.module.scss';

export const Filter = () => {
  const dispatch = useAppDispatch();
  const category = useAppSelector((state) => state.category.category);
  const filters = useAppSelector((state) => state.category.filters);
  const openFiltersModal = useAppSelector((state) => state.category.openAllFilters);
  const prevActiveFilters = useAppSelector((state) => state.category.prevActiveFilters);
  const [abortController, setAbortController] = useState(new AbortController());

  const importantFilters = useMemo(
    () => getImportantFiltersForCategory(filters, category!.translit),
    [category, filters]
  );

  const filterList = Object.keys(importantFilters);

  const getFilteredProducts = () => {
    abortController.abort();
    const newAbortController = new AbortController();
    setAbortController(newAbortController);
    dispatch(submitFilters({ update: true, signal: newAbortController.signal }));
  };

  const getFilteredProductsDebounce = useDebounceCallback(getFilteredProducts, 500);

  const changeRangeHandler = (min: string, max: string, id: string) => {
    dispatch(changeRange({ min, max, id }));
    getFilteredProductsDebounce();
    sendMetrics(Metrics.FILTER_GOAL);
  };

  const changeCheckboxListHandler = (options: OptionType[], id: string) => {
    dispatch(changeList({ arr: options, id }));
    getFilteredProducts();
    sendMetrics(Metrics.FILTER_GOAL);
  };

  const renderFilters = (filter: TransformedCategoryFilterItemType, id: string) => {
    switch (filter.type) {
      case 'String':
        return (
          <FilterSelect
            filterId={id}
            activeFilters={prevActiveFilters}
            key={filter.name}
            options={filter.values || []}
            label={getFilterLabel(id, filter.name)}
            onChange={changeCheckboxListHandler}
          />
        );
      case 'Number':
        return (
          <SelectWithRange
            activeFilters={prevActiveFilters as Record<string, number[]>}
            widthRadioList={id === ID_WIDTH_ATTRIBUTE}
            filterId={id}
            baseMaxValue={filter.max ? filter.max.toString() : ''}
            baseMinValue={filter.min ? filter.min.toString() : ''}
            maxValue={
              prevActiveFilters[id] && prevActiveFilters[id].length > 1 ? prevActiveFilters[id][1].toString() : ''
            }
            minValue={
              prevActiveFilters[id] && prevActiveFilters[id].length > 1 ? prevActiveFilters[id][0].toString() : ''
            }
            onChange={changeRangeHandler}
            key={filter.name}
            label={getFilterLabel(id, filter.name)}
          />
        );
    }
  };

  return (
    <div
      data-testid={'filter'}
      className={cls.wrap}
    >
      <Container className={cls.container}>
        <HStack className={cls.row}>
          <AllFiltersButton className={cls.allFilters} />

          {filterList.length > 0 &&
            filterList.map((filterId) => {
              return renderFilters(filters[filterId], filterId);
            })}
        </HStack>
      </Container>

      {openFiltersModal && <FilterModal />}
    </div>
  );
};
